import { ActionIcon, Divider, Stack, Text, Tooltip } from "@mantine/core"
import { IconCopy, IconMail } from "@tabler/icons-react"
import { notifications } from "@mantine/notifications"
import {
  LabeledValueProps,
  LabeledValue,
} from "../../../../../-components/default-list/labeled-value"
import { Page } from "../../../../../-components/Page"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { Route } from "../$id"
import { LogDto, useGetAuthenticatedUserSuspense } from "@/orval/gen"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"

function formatLogForCopy(
  log: LogDto,
  logDetails: LabeledValueProps[],
  properties: Record<string, unknown>,
  currentUserName: string
): string {
  const divider = "═".repeat(60)
  const sectionDivider = "─".repeat(60)

  let text = `${divider}\nLOG REPORT\n${divider}\n\n`

  // Message
  text += `MESSAGE\n${sectionDivider}\n${log.message}\n\n`

  // Details
  text += `DETAILS\n${sectionDivider}\n`
  logDetails.forEach((detail) => {
    if (detail.value && typeof detail.value === "string") {
      text += `${detail.label}: ${detail.value}\n`
    } else if (detail.value && typeof detail.value === "number") {
      text += `${detail.label}: ${detail.value}\n`
    } else if (detail.value) {
      text += `${detail.label}: ${JSON.stringify(detail.value, null, 2)}\n`
    }
  })

  // Properties
  if (Object.keys(properties).length > 0) {
    text += `\nPROPERTIES\n${sectionDivider}\n`
    Object.entries(properties).forEach(([key, value]) => {
      const valueStr =
        typeof value === "string" ? value : JSON.stringify(value, null, 2)
      text += `${key}: ${valueStr}\n`
    })
  }

  // Footer
  text += `\n${divider}\nGenerated: ${new Date().toLocaleString()} by user: ${currentUserName}\n${divider}\n`

  return text
}

function RenderProperties({ logEvent }: { logEvent: string }) {
  const obj = JSON.parse(logEvent)
  if (obj === null || typeof obj !== "object") {
    return null
  }
  if (obj["Properties"] && typeof obj["Properties"] === "object") {
    const entries: LabeledValueProps[] = Object.entries(obj["Properties"]).map(
      ([key, value]) => ({
        label: key,
        value: typeof value === "string" ? value : JSON.stringify(value),
      })
    )
    return (
      <React.Fragment>
        {entries.map((props, i) => (
          <React.Fragment key={i}>
            <LabeledValue {...props} />
            {i < entries.length - 1 && <Divider size={"xs"} />}
          </React.Fragment>
        ))}
      </React.Fragment>
    )
  }

  return null
}

function getPropertiesObject(logEvent: string): Record<string, unknown> {
  try {
    const obj = JSON.parse(logEvent)
    if (obj?.Properties && typeof obj.Properties === "object") {
      return obj.Properties
    }
  } catch {
    // Ignore parse errors
  }
  return {}
}

export function LogPage() {
  const id = parseInt(Route.useParams().id)
  const { data: log } = useSuspenseQueryGet("/api/Log/GetLog", { id })
  const { data: user } = useBatchedSimpleUser(log.userId)
  const { data: currentUser } = useGetAuthenticatedUserSuspense()
  const { data: currentUserSimple } = useBatchedSimpleUser(currentUser?.userId)
  const logDetails: LabeledValueProps[] = [
    { label: "ID", value: log.id },
    {
      label: "Time stamp",
      value: getFormattedDate(log.timeStamp, "L LTS"),
    },
    { label: "Level", value: log.level },
    {
      label: "Username",
      value: user?.userName ?? "Unknown user",
    },
    ...(log.exception ? [{ label: "Exception", value: log.exception }] : []),
  ]

  const properties = getPropertiesObject(log.logEvent)

  const handleCopyLog = async () => {
    const text = formatLogForCopy(
      log,
      logDetails,
      properties,
      currentUserSimple?.userName ?? "Unknown user"
    )
    try {
      await navigator.clipboard.writeText(text)
      notifications.show({
        position: "top-right",
        autoClose: 3000,
        title: "Success",
        message: "Log copied to clipboard",
        color: "green",
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error"
      notifications.show({
        position: "top-right",
        autoClose: 3000,
        title: "Error",
        message: `Failed to copy log: ${errorMessage}`,
        color: "red",
      })
    }
  }

  const handleSendToSupport = () => {
    const subject = encodeURIComponent(`Support Request: ${log.message}`)
    const body = encodeURIComponent(
      formatLogForCopy(
        log,
        logDetails,
        properties,
        currentUserSimple?.userName ?? "Unknown user"
      )
    )
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank")
  }

  return (
    <Page>
      <Stack gap={"xs"} style={{ whiteSpace: "pre-wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Text size="xl" fw={"bold"} style={{ flex: 1 }}>
            {log.message}
          </Text>
          <Tooltip label="Send to support via email">
            <ActionIcon
              onClick={handleSendToSupport}
              variant="default"
              size="lg"
            >
              <IconMail size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Copy to clipboard for support">
            <ActionIcon onClick={handleCopyLog} variant="default" size="lg">
              <IconCopy size={16} />
            </ActionIcon>
          </Tooltip>
        </div>
        {logDetails.map((props, i) => (
          <React.Fragment key={i}>
            <LabeledValue {...props} />
            {i < logDetails.length - 1 && <Divider size={"xs"} />}
          </React.Fragment>
        ))}

        <Divider
          size={"xl"}
          label={<Text>Properties</Text>}
          labelPosition="left"
        />
        <RenderProperties logEvent={log.logEvent} />
      </Stack>
    </Page>
  )
}
